import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import fetchWrapper from "@/services/fetchWrapper.service";

import useStateHandler from './useStateHandler';

const inflightRequests = new Map();

/**
 * Custom hook used for everything related to getting the data for your components in an efficient way. It uses my own cache system for efficient fetching.
 * @param {string} cacheKey - The key used to store the cached data
 * @param {string} url - The API endpoint to fetch data from
 * @param {Object} options - An optional object containing options to customize the behavior of the hook.
 * @param {boolean} options.refreshOnMount - Refresh a cached value on a new visit.
 * @param {boolean} options.deferInvalidationOnMount - Keep an invalid cached snapshot on return; later invalidation actions still refresh it.
 * @returns {Object} An object that contains the loading state, the requested data and a function to manually trigger a request
 */
export default function useCache(cacheKey, url, options={}) {
  const { cacheHandler, state } = useStateHandler();
  const requestedCursorRef = useRef(null);
  const requestGenerationRef = useRef(0);
  const hasCheckedInitialCacheRef = useRef(false);
  const isDisabled = options.disabled === true;
  const initialData =
    cacheHandler.getCacheValue(cacheKey)?.data
    ?? options.initialData
    ?? null;
  const [fetchData, _setFetchData] = useState(initialData); /** Cached values are stored in render phase to increase speed */
  const [error, setError] = useState(null);
  const [loadMoreError, setLoadMoreError] = useState(null);
  const [loading, setLoading] = useState(
    isDisabled
      ? false
      : (Boolean(fetchData) && (cacheHandler.getCacheValue(cacheKey)?.isDataInvalid === false || Boolean(options.initialData))) ? false : true
  );
  const dependencyArray = options.invalidateWhen ? options.invalidateWhen.map(action => state.triggeredActions[action]) : []; /** This will configure the useEffect to execute again only when one action that invalidates the data triggers */
  dependencyArray.push(isDisabled);

  const navigate = useNavigate();

  function _buildRequestUrl() {
    return `${url}${_getQueryString()}`;
  }

  function _getInflightRequestKey() {
    return JSON.stringify({
      url: _buildRequestUrl(),
      injectToken: options.injectToken === true,
      optionalToken: options.optionalToken === true,
      viewerId: state.user_session?._id ?? null,
    });
  }

  function _getQueryString() {
    let parts = [];

    for(const key in options.includeSearchQueries) {
      const value = options.includeSearchQueries[key];

      if(value) {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
      }
    }

    return '?' + parts.join('&');
  }

  async function _fetchNewData() {
    if (isDisabled) {
      return;
    }
    requestGenerationRef.current += 1;
    requestedCursorRef.current = null;
    setError(null);
    setLoadMoreError(null);
    setLoading(true);

    const requestKey = _getInflightRequestKey();
    let requestPromise = inflightRequests.get(requestKey);

    if (!requestPromise) {
      requestPromise = fetchWrapper.get(_buildRequestUrl(), {
        injectToken: options.injectToken,
        optionalToken: options.optionalToken,
      });
      inflightRequests.set(requestKey, requestPromise);
    }

    try {
      const { response, data } = await requestPromise;
      if (response.ok) {
        setFetchData(data);
      } else {
        setError(new Error(`Request failed (${response.status})`));
        if ([401, 403].includes(response.status)) navigate("/", { replace: true });
      }
    } catch (requestError) {
      setError(requestError);
    } finally {
      if (inflightRequests.get(requestKey) === requestPromise) {
        inflightRequests.delete(requestKey);
      }
      setLoading(false);
    }
  }

  function refetch() { 
    return _fetchNewData();
  }

  async function loadMoreData() {
    if (isDisabled) {
      return;
    }
    const cursor = options.cursorPagination ? fetchData?.nextCursor : null;
    if (options.cursorPagination && (!cursor || requestedCursorRef.current === cursor)) {
      return;
    }
    if (options.cursorPagination) requestedCursorRef.current = cursor;
    setLoadMoreError(null);
    const requestGeneration = requestGenerationRef.current;
    const pageParameter = options.cursorPagination
      ? `cursor=${encodeURIComponent(cursor)}`
      : `offset=${fetchData.data.length}`;
    try {
      const { response, data } = await fetchWrapper.get(`${url}${_getQueryString()}&${pageParameter}`, {
        injectToken: options.injectToken,
        optionalToken: options.optionalToken,
      });

      if (requestGeneration !== requestGenerationRef.current) return;
      if(response.ok) {
        setFetchData({ ...data, data: [...fetchData.data, ...data.data] });
      } else {
        if (options.cursorPagination) requestedCursorRef.current = null;
        setLoadMoreError(new Error(`Request failed (${response.status})`));
        if([401, 403].includes(response.status)) navigate("/", { replace: true });
      }
    } catch (requestError) {
      if (options.cursorPagination && requestGeneration === requestGenerationRef.current) {
        requestedCursorRef.current = null;
      }
      setLoadMoreError(requestError);
    }
  }

  function setFetchData(data) {
    let dataToStore = data;

    _setFetchData(prevValue => {
      if(typeof data === "function") {
        dataToStore = data(prevValue);
      }

      return dataToStore;
    });

    if(typeof dataToStore !== "function") {
      cacheHandler.storeInCache(cacheKey, dataToStore, options); 
    }
  }

  useEffect(() => {
    if (isDisabled) {
      return;
    }
    const cacheValue = cacheHandler.getCacheValue(cacheKey);
    const isInitialCheck = !hasCheckedInitialCacheRef.current;
    hasCheckedInitialCacheRef.current = true;

    if (!cacheValue && options.initialData) {
      cacheHandler.storeInCache(cacheKey, options.initialData, options);
      return;
    }

    if (isInitialCheck && options.deferInvalidationOnMount && cacheValue?.data
      && cacheValue.isDataInvalid && !options.refreshOnMount) {
      setLoading(false);
      return;
    }

    if(!cacheValue || cacheValue.isDataInvalid || options.refreshOnMount) {
      _fetchNewData();
    }
  }, dependencyArray);

  return { loading, error, loadMoreError, fetchData, setFetchData, loadMoreData, refetch };
}
